import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../../context/AuthContext';
import { messagesApi } from '../../utils/api';
import {
  FiHome,
  FiUser,
  FiMessageSquare,
  FiGrid,
  FiFileText,
  FiPlusCircle,
  FiLogOut,
  FiMenu,
  FiX,
  FiUsers,
  FiBriefcase,
  FiCheckSquare,
  FiSend,
  FiBell,
  FiChevronDown,
  FiClock,
  FiCheck
} from 'react-icons/fi';
import './DashboardLayout.css';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profilesDropdownOpen, setProfilesDropdownOpen] = useState(false);

  // Fetch unread messages count for approved creators only
  const { data: messagesData } = useQuery(
    'unread-messages',
    () => messagesApi.getAll({ limit: 1 }),
    {
      enabled: user?.role === 'creator' && user?.status === 'approved',
      refetchInterval: 30000 // Refetch every 30 seconds
    }
  );

  const unreadCount = messagesData?.data?.unreadCount || 0;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isApprovedCreator = user?.role === 'creator' && user?.status === 'approved';

  // Creator Navigation - limited for pending creators
  const creatorLinks = isApprovedCreator
    ? [
        { to: '/dashboard', icon: FiHome, label: 'ראשי', end: true },
        { to: '/dashboard/explore', icon: FiGrid, label: 'קמפיינים' },
        { to: '/dashboard/applications', icon: FiFileText, label: 'ההגשות שלי' },
        { to: '/dashboard/messages', icon: FiMessageSquare, label: 'הודעות' },
        { to: '/dashboard/profile', icon: FiUser, label: 'פרופיל' }
      ]
    : [
        { to: '/dashboard', icon: FiHome, label: 'ראשי', end: true },
        { to: '/dashboard/profile', icon: FiUser, label: 'פרופיל' }
      ];

  // Brand Navigation
  const brandLinks = [
    { to: '/dashboard', icon: FiHome, label: 'ראשי', end: true },
    { to: '/dashboard/campaigns', icon: FiGrid, label: 'הקמפיינים שלי', end: true },
    { to: '/dashboard/campaigns/create', icon: FiPlusCircle, label: 'קמפיין חדש' },
    { to: '/dashboard/messages', icon: FiMessageSquare, label: 'הודעות' },
    { to: '/dashboard/profile', icon: FiUser, label: 'פרופיל' }
  ];

  // Check if current route is profiles related
  const isProfilesActive = location.pathname.includes('/admin/creators');

  return (
    <div className="dashboard-layout">
      {/* Mobile Header */}
      <header className="dashboard-mobile-header">
        <button
          className="menu-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <FiX /> : <FiMenu />}
        </button>
        <div className="logo">
          <img src="/logo-white.png" alt="Weon" className="logo-image" />
        </div>
        {user?.role === 'creator' && (
          <NavLink to="/dashboard/messages" className="notification-bell">
            <FiBell />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </NavLink>
        )}
      </header>

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <img src="/logo-white.png" alt="Weon" className="logo-image" />
          </div>
        </div>

        {/* User Info */}
        <div className="sidebar-user">
          <div className="user-avatar">
            {user?.profileImage ? (
              <img src={user.profileImage} alt={user.displayName || user.fullName} />
            ) : (
              <span>{(user?.displayName || user?.fullName || 'U').charAt(0)}</span>
            )}
          </div>
          <div className="user-info">
            <span className="user-name">{user?.displayName || user?.fullName?.split(' ')[0]}</span>
            <span className="user-role">
              {user?.role === 'admin' ? 'מנהל' : user?.role === 'creator'
                ? (user?.status === 'approved' ? 'יוצר/ת תוכן' : 'ממתין לאישור')
                : 'מותג'}
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {user?.role === 'admin' ? (
            <>
              {/* Admin Navigation */}
              <NavLink
                to="/dashboard"
                end
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <FiHome className="nav-icon" />
                <span>ראשי</span>
              </NavLink>

              <NavLink
                to="/dashboard/admin/campaigns"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <FiGrid className="nav-icon" />
                <span>קמפיינים</span>
              </NavLink>

              <NavLink
                to="/dashboard/admin/applications"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <FiCheckSquare className="nav-icon" />
                <span>הגשות</span>
              </NavLink>

              {/* Creators Dropdown */}
              <div className="nav-dropdown">
                <button
                  className={`nav-link nav-dropdown-toggle ${isProfilesActive ? 'active' : ''}`}
                  onClick={() => setProfilesDropdownOpen(!profilesDropdownOpen)}
                >
                  <FiUsers className="nav-icon" />
                  <span>יוצרים</span>
                  <FiChevronDown className={`dropdown-arrow ${profilesDropdownOpen ? 'open' : ''}`} />
                </button>
                {profilesDropdownOpen && (
                  <div className="nav-dropdown-menu">
                    <NavLink
                      to="/dashboard/admin/creators/pending"
                      className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <FiClock className="nav-icon" />
                      <span>ממתינים לאישור</span>
                    </NavLink>
                    <NavLink
                      to="/dashboard/admin/creators/approved"
                      className={({ isActive }) => `nav-dropdown-item ${isActive ? 'active' : ''}`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <FiCheck className="nav-icon" />
                      <span>מאושרים</span>
                    </NavLink>
                  </div>
                )}
              </div>

              <NavLink
                to="/dashboard/admin/brands"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <FiBriefcase className="nav-icon" />
                <span>מותגים</span>
              </NavLink>

              <NavLink
                to="/dashboard/admin/messages"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <FiSend className="nav-icon" />
                <span>שלח הודעה</span>
              </NavLink>
            </>
          ) : (
            // Creator/Brand Navigation
            (user?.role === 'brand' ? brandLinks : creatorLinks).map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  `nav-link ${isActive ? 'active' : ''}`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <link.icon className="nav-icon" />
                <span>{link.label}</span>
                {link.to === '/dashboard/messages' && user?.role === 'creator' && unreadCount > 0 && (
                  <span className="nav-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </NavLink>
            ))
          )}
        </nav>

        {/* Logout */}
        <button className="logout-btn" onClick={handleLogout}>
          <FiLogOut className="nav-icon" />
          <span>התנתק</span>
        </button>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="dashboard-main">
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;
